/**
 * Sprint 5 inter-question milestone banner schedule.
 *
 * The quiz shows a full-width motivational banner AFTER the user answers
 * the milestone questions below. Each entry points at a microcopy id from
 * MICROCOPY_POOL in homepage-variants.ts — copy is never inlined here so
 * the doc-sync test that locks MICROCOPY_POOL keeps owning the strings.
 *
 * Schedule (per the Sprint 5 quiz brief):
 *   Q5  → mc_pattern_clearer
 *   Q10 → mc_few_steps
 *   Q15 → mc_top_three_leaks
 *   Q19 → mc_one_next_step
 *
 * The map is keyed by the 1-based question index AFTER which the banner
 * appears. quiz-milestones.test.ts asserts every id resolves against the
 * live pool, so a renamed pool id fails the build instead of silently
 * dropping a banner.
 */

import { MICROCOPY_POOL, type MicrocopyLine } from "@/lib/content/homepage-variants";

export interface QuizMilestone {
  /** 1-based question index. The banner shows once this question is answered. */
  readonly afterIndex: number;
  /** Stable id into MICROCOPY_POOL. */
  readonly microcopyId: string;
}

export const QUIZ_MILESTONES: ReadonlyArray<QuizMilestone> = [
  { afterIndex: 5, microcopyId: "mc_pattern_clearer" },
  { afterIndex: 10, microcopyId: "mc_few_steps" },
  { afterIndex: 15, microcopyId: "mc_top_three_leaks" },
  { afterIndex: 19, microcopyId: "mc_one_next_step" },
];

const POOL_BY_ID: Readonly<Record<string, MicrocopyLine>> = Object.freeze(
  Object.fromEntries(MICROCOPY_POOL.map((m) => [m.id, m])),
);

/** Resolve a pool id to its line, or undefined if the id is unknown. */
export function microcopyLineById(id: string): string | undefined {
  return POOL_BY_ID[id]?.line;
}

/**
 * Return the milestone banner line to show AFTER answering question
 * `index` (1-based), or null if that index has no milestone. Throws if a
 * configured id is missing from the pool — that is a wiring bug we want
 * loud at the call site, mirrored by the unit test.
 */
export function milestoneBannerForIndex(index: number): string | null {
  const hit = QUIZ_MILESTONES.find((m) => m.afterIndex === index);
  if (!hit) return null;
  const line = microcopyLineById(hit.microcopyId);
  if (line === undefined) {
    throw new Error(
      `QUIZ_MILESTONES references unknown microcopy id "${hit.microcopyId}". ` +
        "Fix quiz-milestones.ts or MICROCOPY_POOL.",
    );
  }
  return line;
}
